import type { TranslationFunction } from "@src/packages/i18n";
import type { CycleType } from "../hooks/useCycles";
import type { CycleHeaderState } from "../hooks/resolveCycleHeaderState";
import { formatPhaseTime, formatRelativePhaseDate } from "./formatPhaseDate";

export interface CareHeaderCopy {
  eyebrow: string;
  title: string;
  summary: string;
  hasActiveCycle: boolean;
}

const CARE_HEADER_PREFIX = "care.overview.header";
const CARE_OVERVIEW_PREFIX = "care.overview";

const SUMMARY_KEY_BY_TYPE: Partial<Record<CycleType, string>> = {
  egg_freezing: `${CARE_OVERVIEW_PREFIX}.summary.egg_freezing`,
  sperm_freezing: `${CARE_OVERVIEW_PREFIX}.summary.sperm_freezing`,
  iui: `${CARE_OVERVIEW_PREFIX}.summary.iui`,
  ivf: `${CARE_OVERVIEW_PREFIX}.summary.ivf`,
  icsi: `${CARE_OVERVIEW_PREFIX}.summary.icsi`,
  fet: `${CARE_OVERVIEW_PREFIX}.summary.fet`,
  embryo_freezing: `${CARE_OVERVIEW_PREFIX}.summary.embryo_freezing`,
};

function summaryFor(
  t: TranslationFunction,
  cycleType: CycleType | undefined,
): string {
  const key = cycleType ? SUMMARY_KEY_BY_TYPE[cycleType] : undefined;
  return key ? t(key) : t(`${CARE_OVERVIEW_PREFIX}.summary_default`);
}

function eyebrowFor(
  t: TranslationFunction,
  cycleType: CycleType | undefined,
  cycleLabels: Partial<Record<CycleType, string>>,
): string {
  if (!cycleType) return t(`${CARE_OVERVIEW_PREFIX}.eyebrow_care`);
  const label = cycleLabels[cycleType] ?? cycleType.replace(/_/g, " ");
  return t(`${CARE_OVERVIEW_PREFIX}.eyebrow_current_cycle`, {
    params: { type: label.toLowerCase() },
  });
}

export function presentCycleHeaderForCare(args: {
  state: CycleHeaderState;
  t: TranslationFunction;
  locale: string | undefined;
  cycleLabels: Partial<Record<CycleType, string>>;
  now?: Date;
}): CareHeaderCopy {
  const { state, t, locale, cycleLabels } = args;
  const now = args.now ?? new Date();

  if (state.kind === "no_cycle") {
    return {
      eyebrow: t(`${CARE_OVERVIEW_PREFIX}.eyebrow_care`),
      title: t(`${CARE_OVERVIEW_PREFIX}.pre_cycle_title`),
      summary: t(`${CARE_OVERVIEW_PREFIX}.pre_cycle_summary`),
      hasActiveCycle: false,
    };
  }

  const eyebrow = eyebrowFor(t, state.cycleType, cycleLabels);
  const summary =
    state.kind === "stim_trigger_logged"
      ? t(`${CARE_HEADER_PREFIX}.stim_trigger_logged_summary`)
      : summaryFor(t, state.cycleType);
  const hasActiveCycle = state.kind !== "cycle_closed";

  let title: string;
  switch (state.kind) {
    case "cycle_closed":
      title = t(`${CARE_HEADER_PREFIX}.cycle_closed_title`);
      break;
    case "pre_treatment":
      title = t(`${CARE_HEADER_PREFIX}.pre_treatment_title`, {
        params: { day: String(state.dayInPhase) },
      });
      break;
    case "stim_trigger_logged":
      title = t(`${CARE_HEADER_PREFIX}.stim_trigger_logged_title`);
      break;
    case "stim_monitoring_next": {
      const when = formatRelativePhaseDate(state.apptDate, locale, t, { now });
      title = t(`${CARE_HEADER_PREFIX}.stim_monitoring_title`, {
        params: { when },
      });
      break;
    }
    case "stim_med_next": {
      const when = formatRelativePhaseDate(state.medDate, locale, t, { now });
      const time = formatPhaseTime(state.medTime, locale);
      title = t(`${CARE_HEADER_PREFIX}.stim_med_title`, {
        params: { when, time },
      });
      break;
    }
    case "stim_generic":
      title = t(`${CARE_HEADER_PREFIX}.stim_generic_title`, {
        params: { day: String(state.dayInPhase) },
      });
      break;
    case "retrieval_scheduled": {
      const when = formatRelativePhaseDate(state.retrievalDate, locale, t, {
        now,
      });
      title = t(`${CARE_HEADER_PREFIX}.retrieval_scheduled_title`, {
        params: { when },
      });
      break;
    }
    case "retrieval_done_pending":
      title = t(`${CARE_HEADER_PREFIX}.retrieval_done_pending_title`);
      break;
    case "fertilization":
      title = t(`${CARE_HEADER_PREFIX}.fertilization_title`);
      break;
    case "embryo_dev":
      title = t(`${CARE_HEADER_PREFIX}.embryo_dev_title`, {
        params: { day: String(state.dayInPhase) },
      });
      break;
    case "transfer_scheduled": {
      const when = formatRelativePhaseDate(state.transferDate, locale, t, {
        now,
      });
      title = t(`${CARE_HEADER_PREFIX}.transfer_scheduled_title`, {
        params: { when },
      });
      break;
    }
    case "beta_scheduled": {
      const when = formatRelativePhaseDate(state.betaDate, locale, t, { now });
      title = t(`${CARE_HEADER_PREFIX}.beta_scheduled_title`, {
        params: { when },
      });
      break;
    }
    case "beta_pending":
      title = t(`${CARE_HEADER_PREFIX}.beta_pending_title`);
      break;
    case "unknown_active":
      title = t(`${CARE_HEADER_PREFIX}.unknown_active_title`);
      break;
    // Says only that the result is saved, never what it was: this header is
    // shared across every ending, so naming one would celebrate a loss.
    case "result_recorded":
      title = t(`${CARE_HEADER_PREFIX}.result_recorded_title`);
      break;
  }

  return { eyebrow, title, summary, hasActiveCycle };
}
